import { useState, useMemo, useRef } from "react";
import { Box, Typography, MenuItem } from "@mui/material";
import { CalendarClock, ChevronDown, Plus } from "lucide-react";
import { Task } from "protogen/tasks_pb";
import { CalendarEvent } from "protogen/calendar_pb";
import PopperMenu from "../common/PopperMenu";

interface TaskRemindersDropdownProps {
  task?: Task;
  events?: CalendarEvent[];
  onAddEvent?: () => void;
  onEditEvent?: (event: CalendarEvent) => void;
}

const TaskRemindersDropdown = ({
  events = [],
  onAddEvent,
  onEditEvent,
}: TaskRemindersDropdownProps) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const taskEvents = useMemo(() => {
    return [...events]
      .filter((e) => e.sourceEntityType === "task_reminder")
      .sort((a, b) => Number(a.createdSec) - Number(b.createdSec));
  }, [events]);

  const totalReminders = taskEvents.length;

  const handleClick = () => {
    if (totalReminders === 0) {
      // No reminders or due date - open the add dialog directly
      onAddEvent?.();
    } else {
      setMenuOpen(true);
    }
  };

  const handleClose = () => {
    setMenuOpen(false);
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "8px 16px",
          borderRadius: "100px",
          border: "1px solid #D4D4D4",
          cursor: "pointer",
          minWidth: "160px",
          height: "40px",
          "&:hover": {
            backgroundColor: "#F5F5F5",
          },
        }}
      >
        <CalendarClock size={20} color="#8E9598" strokeWidth="2px" />
        <Typography
          variant="bodySmall"
          sx={{
            fontSize: "14px",
            fontWeight: "500",
            color: "#8E9598",
            flexGrow: 1,
          }}
        >
          {totalReminders === 0
            ? "No reminders"
            : `${totalReminders} reminder${totalReminders > 1 ? "s" : ""}`}
        </Typography>
        {totalReminders > 0 && (
          <ChevronDown size={16} color="#8E9598" strokeWidth="2px" />
        )}
      </Box>

      <PopperMenu
        open={menuOpen}
        anchorRef={anchorRef}
        handleClose={handleClose}
        placement="bottom-start"
      >
        <>
          {taskEvents.map((event) => {
            const eventDate = new Date(Number(event.startSec) * 1000);
            const metadata = event.meta ? JSON.parse(event.meta) : {};
            const isDueDate = metadata.taskEventType === "dueDate";

            return (
              <MenuItem
                key={event.ref}
                onClick={() => {
                  handleClose();
                  onEditEvent?.(event);
                }}
                sx={(theme) => ({
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "12px",
                  padding: "12px 16px",
                  minWidth: "280px",
                  borderBottom: `1px solid ${theme.palette.border}`,
                  "&:last-child": {
                    borderBottom: "none",
                  },
                  "&:hover": {
                    background: "#FAF9FA",
                  },
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "32px",
                    height: "32px",
                    borderRadius: "8px",
                    backgroundColor: "#F0F9F9",
                    flexShrink: 0,
                  }}
                >
                  <CalendarClock size={16} color="#198282" />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="bodySmall" sx={{ marginBottom: "2px" }}>
                    {isDueDate ? "Due date" : event.title}
                  </Typography>
                  <Typography variant="bodySmall" color="text.secondary">
                    {eventDate.toLocaleDateString(undefined, {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })}
                    {!event.allDay &&
                      ` at ${eventDate.toLocaleTimeString(undefined, {
                        hour: "numeric",
                        minute: "2-digit",
                      })}`}
                  </Typography>
                </Box>
              </MenuItem>
            );
          })}

          <MenuItem
            onClick={() => {
              handleClose();
              onAddEvent?.();
            }}
            sx={{
              padding: "8px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              "&:hover": {
                background: "#FAF9FA",
              },
            }}
          >
            <Plus size={16} color="#198282" />
            <Typography variant="body" color="primary.main">
              Add reminder
            </Typography>
          </MenuItem>
        </>
      </PopperMenu>
    </>
  );
};

export default TaskRemindersDropdown;
